/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from 'react';

import { Hcp } from '@/core/graphql/generated/types';
import { isOfTypeHco } from '@/utils/hco/isOfTypeHco';
import { useHcoUrlParams } from '@/utils/hco/useHcoUrlParams';

import {
	GetHcoByIdQuery,
	useGetHcoByIdLazyQuery
} from '../_graphql/GetHCOById.hook';

export type HcoProfile = Extract<
	GetHcoByIdQuery['hco'],
	{ __typename?: 'HCO' }
>;

const DEFAULT_AFFILIATIONS_PAGE_SIZE = 5;

type UseHcoDataOptions = {
	suppressErrors?: boolean;
	affiliationsPageSize?: number;
	enabled?: boolean;
	includeDcrPartners?: boolean;
};

export const useHcoData = ({
	suppressErrors = false,
	affiliationsPageSize = DEFAULT_AFFILIATIONS_PAGE_SIZE,
	enabled = true,
	includeDcrPartners = false
}: UseHcoDataOptions = {}) => {
	const { id } = useHcoUrlParams(suppressErrors);

	const [getHcoById, { data, loading: queryLoading }] = useGetHcoByIdLazyQuery({
		fetchPolicy: 'no-cache'
	});
	const [sessionData, setSessionData] = useState<GetHcoByIdQuery | undefined>();

	useEffect(() => {
		if (id && enabled) {
			setSessionData(undefined);
			getHcoById({
				variables: { vid: id, affiliationsPageSize, includeDcrPartners }
			});
		} else if (!enabled) {
			setSessionData(undefined);
		}
	}, [getHcoById, id, affiliationsPageSize, enabled, includeDcrPartners]);

	useEffect(() => {
		if (enabled && data) setSessionData(data);
	}, [enabled, data]);

	const loading = enabled && (queryLoading || (!!id && !sessionData));
	const hco = sessionData?.hco;

	const affiliatedHcpSearch =
		hco?.__typename === 'HCO' &&
		hco.affiliatedHcpSearch.__typename === 'HCOAffiliatedHcpsResults'
			? hco.affiliatedHcpSearch
			: null;

	const affiliateHcpCount = affiliatedHcpSearch?.paginationMetadata.total ?? 0;
	const affiliateInactiveHcpCount = affiliatedHcpSearch?.inactiveCount ?? 0;
	const affiliatedHcps = (affiliatedHcpSearch?.results ?? []) as Hcp[];

	if (
		!loading &&
		hco &&
		'__typename' in hco &&
		hco.__typename === 'ResourceNotFound'
	) {
		return {
			hco: null,
			loading,
			isInvalidHco: true
		};
	}

	return {
		hco: isOfTypeHco(hco) ? hco : null,
		affiliateHcpCount,
		affiliateInactiveHcpCount,
		affiliatedHcps,
		loading,
		isInvalidHco: false
	};
};
